import type { IVector3 } from '@seddi/math-v2';
import { type Fabric, FabricSide } from '@seddi/scene';
import type { LoadingManager, WebGLRenderer } from '@seddi/three';
import type { RecolorContext } from '@seddi/ui-recolor';
import { produce } from 'immer';

import { adapt } from './fabricAdapter';
import type { FabricGfx } from './fabricTypes';
import { load as loadMaterial, loadRecolor as loadMaterialRecolor } from './materialLoader';

type LoadOptions = { mipmapsHighQuality?: boolean; mipmapsUnsharp?: boolean };

/**
 * Load fabric.
 * @param desc - Fabric.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param options - Load configuration.
 */
export const loadRaw = async (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  options: LoadOptions,
): Promise<FabricGfx> => {
  // Adapt fabric descriptor to materials descriptors.
  const doubleSided = adapt(desc);
  const materialsDesc = [doubleSided.front];
  if (doubleSided.back) {
    materialsDesc.push(doubleSided.back);
  }

  // Load materials.
  const materialOptions = {
    mipmapsHighQuality: options.mipmapsHighQuality ?? true,
    mipmapsUnsharp: options.mipmapsUnsharp ?? false,
  };
  const materials = await Promise.all(
    materialsDesc.map((desc) => loadMaterial(desc, manager, renderer, materialOptions)),
  );

  // Return.
  const fabric: FabricGfx = { front: materials[0] };
  if (materials.length === 2) {
    fabric.back = materials[1];
  }
  return fabric;
};

/**
 * Load fabric with albedo recolor.
 * @param desc - Fabric.
 * @param manager Loading manager.
 * @param renderer - WebGL renderer.
 * @param color - Target color.
 * @param recolor - Recolor context.
 * @param options - Load configuration.
 */
export const loadRecolor = async (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  color: IVector3,
  recolor: RecolorContext,
  options: LoadOptions,
): Promise<FabricGfx> => {
  // Adapt fabric descriptor to materials descriptors.
  const doubleSided = adapt(desc);
  const materialDescs = [doubleSided.front];
  if (doubleSided.back) {
    materialDescs.push(doubleSided.back);
  }

  // Load materials
  const materialOptions = {
    mipmapsHighQuality: options.mipmapsHighQuality ?? true,
    mipmapsUnsharp: options.mipmapsUnsharp ?? false,
  };
  // NOTE: ID comes formatted with color, validate it makes sense outside this function.
  const fabricId = desc.id;
  const promises = materialDescs.map(async (desc, i) => {
    // NOTE: Validate if textures can come with colliding IDs.
    const desc_ = produce(desc, (desc) => {
      const _id = `${fabricId.split(':')[0]}_${i === 0 ? FabricSide.FRONT : FabricSide.BACK}`;
      desc.params.baseColorMap!.id = _id;
    });
    return loadMaterialRecolor(desc_, manager, renderer, color, recolor, materialOptions);
  });
  const materials = await Promise.all(promises);

  const fabric: FabricGfx = { front: materials[0] };
  if (materials.length === 2) {
    fabric.back = materials[1];
  }
  return fabric;
};

/**
 * Load fabric with optional albedo recolor.
 * @param desc - Fabric.
 * @param manager - Loading manager.
 * @param renderer - WebGL renderer.
 * @param color - Target color.
 * @param recolor - Recolor context.
 * @param options - Load configuration.
 */
export const load = (
  desc: Fabric,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  options: LoadOptions,
  color?: IVector3,
  recolor?: RecolorContext,
) => {
  if (color) {
    return loadRecolor(desc, manager, renderer, color, recolor!, options);
  }
  return loadRaw(desc, manager, renderer, options);
};
